import { Alert, AlertColor, Snackbar } from '@mui/material';
import React, { createContext, useState } from 'react';

type SnackbarContextType = {
  show: (
    text: string,
    severity?: AlertColor,
    autoHideDuration?: number,
    vertical?: 'top' | 'bottom',
    horizontal?: 'center' | 'left' | 'right',
  ) => void;
  info: (
    text: string,
    autoHideDuration?: number,
    vertical?: 'top' | 'bottom',
    horizontal?: 'center' | 'left' | 'right',
  ) => void;
  warning: (
    text: string,
    autoHideDuration?: number,
    vertical?: 'top' | 'bottom',
    horizontal?: 'center' | 'left' | 'right',
  ) => void;
  success: (
    text: string,
    autoHideDuration?: number,
    vertical?: 'top' | 'bottom',
    horizontal?: 'center' | 'left' | 'right',
  ) => void;
  error: (
    text: string,
    autoHideDuration?: number,
    vertical?: 'top' | 'bottom',
    horizontal?: 'center' | 'left' | 'right',
  ) => void;
};

const AUTO_HIDE_DURATION = 5000;

const SnackbarContext = createContext<SnackbarContextType | null>(null);
type AlertProperty = {
  text: string;
  severity: AlertColor | undefined;
  autoHideDuration: number | undefined;
  vertical?: 'top' | 'bottom';
  horizontal?: 'center' | 'left' | 'right';
};
export const SnackbarProvider = ({ children }: { children: React.ReactElement }) => {
  const [data, setData] = useState<AlertProperty>({
    vertical: 'bottom',
    horizontal: 'center',
  } as AlertProperty);

  const show = (
    text: string,
    severity: AlertColor | undefined,
    autoHideDuration: number | undefined,
    vertical?: 'top' | 'bottom',
    horizontal?: 'center' | 'left' | 'right',
  ) => {
    setData({
      text,
      severity: severity || 'info',
      autoHideDuration:
        typeof autoHideDuration === 'number' ? autoHideDuration : AUTO_HIDE_DURATION,
      vertical: vertical || data.vertical,
      horizontal: horizontal || data.horizontal,
    });
  };

  const warning = (
    text: string,
    autoHideDuration?: number,
    vertical?: 'top' | 'bottom',
    horizontal?: 'center' | 'left' | 'right',
  ) => {
    show(text, 'warning', autoHideDuration, vertical, horizontal);
  };

  const error = (
    text: string,
    autoHideDuration?: number,
    vertical?: 'top' | 'bottom',
    horizontal?: 'center' | 'left' | 'right',
  ) => {
    show(text, 'error', autoHideDuration, vertical, horizontal);
  };

  const success = (
    text: string,
    autoHideDuration?: number,
    vertical?: 'top' | 'bottom',
    horizontal?: 'center' | 'left' | 'right',
  ) => {
    show(text, 'success', autoHideDuration, vertical, horizontal);
  };

  const info = (
    text: string,
    autoHideDuration?: number,
    vertical?: 'top' | 'bottom',
    horizontal?: 'center' | 'left' | 'right',
  ) => {
    show(text, 'info', autoHideDuration, vertical, horizontal);
  };

  const handleClose = () => {
    setData({ vertical: 'bottom', horizontal: 'center' } as AlertProperty);
  };

  return (
    <SnackbarContext.Provider value={{ show, info, error, success, warning }}>
      {children}

      {data.text && (
        <Snackbar
          open
          anchorOrigin={{ vertical: data.vertical!, horizontal: data.horizontal! }}
          autoHideDuration={data.autoHideDuration}
          onClose={handleClose}
        >
          <Alert onClose={handleClose} severity={data.severity} sx={{ width: '100%' }}>
            {data.text}
          </Alert>
        </Snackbar>
      )}
    </SnackbarContext.Provider>
  );
};

export default SnackbarContext;
